import { useEffect, useMemo, useRef, type MouseEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useCharacter } from "../character";
import { ChoiceCard } from "../components/ChoiceCard";
import { MessageList, useQuickAction } from "../components/MessageList";
import { PendingChoicesContext, type PendingChoices } from "../pendingChoices";
import { systemUi } from "../registry";
import { source, text } from "../text";

const stateLabel = { notStarted: "offen", open: "offen", complete: "erledigt", problem: "Problem" } as const;
const stateMark = { notStarted: "", open: "…", complete: "✓", problem: "!" } as const;

/** The core wizard frame: step list with status, the module's step component, open choices and messages of the step. */
export function WizardPage() {
  const { step: stepParam } = useParams();
  const { character, system, apply, language } = useCharacter();
  const runAction = useQuickAction();
  const navigate = useNavigate();
  const submits = useRef(new Map<string, () => Promise<boolean>>());
  const pendingChoices = useMemo<PendingChoices>(
    () => ({ register: (id, submit) => (submit ? submits.current.set(id, submit) : submits.current.delete(id)) }),
    [],
  );
  /** Leaving the step first applies choices that are filled in but not yet sent, so nothing typed gets lost. */
  const go = async (to: string) => {
    const results = await Promise.all([...submits.current.values()].map((submit) => submit()));
    if (results.every(Boolean)) {
      void navigate(to);
      window.scrollTo({ top: 0 });
    }
  };
  const follow = (to: string) => (e: MouseEvent) => {
    e.preventDefault();
    void go(to);
  };
  const steps = system?.wizardSteps ?? [];
  const statuses = new Map(character.evaluation.steps.map((s) => [s.step, s]));
  // Without a step in the address: a new character starts at the first step; once work has begun, untouched optional steps are passed over.
  const begun = steps.some((s) => statuses.get(s.id)?.state === "complete");
  const firstOpen =
    steps.find((s) => {
      const state = statuses.get(s.id)?.state ?? "notStarted";
      return state !== "complete" && !(begun && s.optional && state === "notStarted");
    }) ?? steps[0];
  const current = steps.find((s) => s.id === stepParam) ?? firstOpen;
  // The step goes into the address at once, so a change in the step states does not move the user to another step.
  useEffect(() => {
    if (!stepParam && current) void navigate(`/c/${character.id}/assistent/${current.id}`, { replace: true });
  }, [stepParam, current?.id, character.id, navigate]);
  if (!current) return <p className="muted">Dieses System hat keinen Assistenten.</p>;
  const index = steps.indexOf(current);
  const ui = systemUi(character.system);
  const StepComponent = ui?.wizardSteps?.[current.id];
  const choices = character.evaluation.openChoices.filter((c) => c.step === current.id && !ui?.handlesChoice?.(c));
  const messages = character.evaluation.messages.filter((m) => m.message.target?.step === current.id);
  const base = `/c/${character.id}`;
  const firstTab = ui?.sheetTabs[0];
  const next = steps[index + 1];
  const previous = steps[index - 1];
  const done = steps.filter((s) => statuses.get(s.id)?.state === "complete").length;

  return (
    <div className="wizard">
      <nav className="wizard-steps" aria-label="Schritte">
        <p className="wizard-progress muted small">
          {done} von {steps.length} Schritten erledigt
        </p>
        <ol>
          {steps.map((s, i) => {
            const status = statuses.get(s.id);
            const state = status?.state ?? "notStarted";
            return (
              <li key={s.id} className={`${s.id === current.id ? "current " : ""}${state}`}>
                <Link to={`${base}/assistent/${s.id}`} onClick={follow(`${base}/assistent/${s.id}`)} aria-current={s.id === current.id ? "step" : undefined}>
                  <span className="step-number">
                    {i + 1}
                    <span className="step-mark" aria-hidden="true">
                      {stateMark[state]}
                    </span>
                  </span>
                  <span className="step-text">
                    <span className="step-label">{text(s.label, language)}</span>
                    <span className="step-state">
                      {state === "notStarted" && s.optional ? "optional" : stateLabel[state]}
                      {status && status.openChoices > 0 && ` · ${status.openChoices} ${status.openChoices === 1 ? "Wahl" : "Wahlen"}`}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      </nav>

      <section className="wizard-step">
        <header className="step-header">
          <div>
            <p className="step-kicker muted small">
              Schritt {index + 1} von {steps.length}
              {current.source && <> · {source(current.source)}</>}
            </p>
            <h2>{text(current.label, language)}</h2>
            {current.description && <p className="step-description muted">{text(current.description, language)}</p>}
          </div>
          {firstTab && (
            <Link to={`${base}/bogen/${firstTab.id}`} onClick={follow(`${base}/bogen/${firstTab.id}`)} className="link small">
              Freie Bearbeitung
            </Link>
          )}
        </header>
        <MessageList
          messages={messages}
          onAcknowledge={(m) => void apply({ type: "core.acknowledge", payload: { key: m.key } })}
          onUnacknowledge={(m) => void apply({ type: "core.unacknowledge", payload: { key: m.key } })}
          onAction={runAction}
        />
        <PendingChoicesContext.Provider value={pendingChoices}>
          {StepComponent && <StepComponent step={current.id} />}
          {choices.length > 0 && StepComponent && <h3 className="section-title">Noch zu entscheiden</h3>}
          {choices.map((choice) => (
            <ChoiceCard key={choice.id} choice={choice} />
          ))}
        </PendingChoicesContext.Provider>
        <footer className="wizard-footer">
          <button type="button" className="button secondary" disabled={!previous} onClick={() => previous && void go(`${base}/assistent/${previous.id}`)}>
            Zurück
          </button>
          {next ? (
            <button type="button" className="button primary" onClick={() => void go(`${base}/assistent/${next.id}`)}>
              Weiter: {text(next.label, language)}
            </button>
          ) : (
            <Link to={`${base}/einstellungen#karriere`} onClick={follow(`${base}/einstellungen#karriere`)} className="button primary">
              Erschaffung abschließen
            </Link>
          )}
        </footer>
      </section>
    </div>
  );
}
